// Réglage des seuils d'alerte.
//
// Parti pris : on règle une MESURE, pas une règle technique. La tension se
// règle donc comme une plage (« entre 207 et 253 V »), alors qu'elle est
// stockée comme deux règles distinctes (gt / lt) côté base.
//
// Surtout, la valeur mesurée en ce moment est affichée en face du seuil, avec
// une jauge. Sans elle, impossible de juger si un seuil est pertinent : 38 °C
// n'a aucun sens tant qu'on ne sait pas que l'appareil tourne à 52 °C.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { alertsApi, alertTitle, METRIC_LABELS, metricLabel, type AlertEvent, type AlertRule, type AlertSeverity } from '../api/alerts';
import { wattelEMApi, type DeviceOverview } from '../api/wattelEM';
import { displaySn } from '../utils/deviceDisplay';
import { RoleGuard } from '../components/RoleGuard';
import { useI18n } from '../i18n';

// ─── Présentation par mesure ───
// domain = échelle de la jauge, choisie pour couvrir les valeurs plausibles.
const METRIC_UI: Record<string, {
  icon: string;
  domain: [number, number];
  decimals: number;
  telemetryKey: 'temperature_c' | 'voltage_v' | 'current_a' | 'power_kw' | 'power_factor' | 'frequency_hz';
}> = {
  temperature_c: { icon: '🌡️', domain: [0, 100], decimals: 1, telemetryKey: 'temperature_c' },
  voltage_v: { icon: '⚡', domain: [180, 280], decimals: 1, telemetryKey: 'voltage_v' },
  current_a: { icon: '〰️', domain: [0, 64], decimals: 2, telemetryKey: 'current_a' },
  power_kw: { icon: '⚡', domain: [0, 22], decimals: 3, telemetryKey: 'power_kw' },
  power_factor: { icon: 'φ', domain: [0, 1], decimals: 3, telemetryKey: 'power_factor' },
  frequency_hz: { icon: '∿', domain: [45, 55], decimals: 1, telemetryKey: 'frequency_hz' },
};

function severityLabel(t: ReturnType<typeof useI18n>['t'], s: AlertSeverity): string {
  const labels: Record<AlertSeverity, string> = {
    critical: t('team_severity_critical'),
    warning: t('team_severity_warning'),
    info: t('team_severity_info'),
  };
  return labels[s] ?? s;
}

/** Un groupe = une mesure sur un appareil, avec sa règle haute et/ou basse. */
interface MetricGroup {
  metric: string;
  max: AlertRule | null; // règle 'gt'
  min: AlertRule | null; // règle 'lt'
}

interface Draft {
  max: string;
  min: string;
  durationMin: string;
  severity: AlertSeverity;
  enabled: boolean;
}

function groupDraft(g: MetricGroup): Draft {
  const ref = g.max ?? g.min!;
  return {
    max: g.max ? String(g.max.threshold) : '',
    min: g.min ? String(g.min.threshold) : '',
    durationMin: String(Math.round(ref.minDurationS / 60)),
    severity: ref.severity,
    enabled: ref.enabled,
  };
}

function draftEquals(g: MetricGroup, d: Draft): boolean {
  const ref = g.max ?? g.min!;
  return (
    (!g.max || Number(d.max) === g.max.threshold) &&
    (!g.min || Number(d.min) === g.min.threshold) &&
    Number(d.durationMin) * 60 === ref.minDurationS &&
    d.severity === ref.severity &&
    d.enabled === ref.enabled
  );
}

/** Position en % d'une valeur sur l'échelle de la jauge. */
function pct(value: number, [lo, hi]: [number, number]): number {
  return Math.min(100, Math.max(0, ((value - lo) / (hi - lo)) * 100));
}

const HISTORY_HOURS = 168; // journal : 7 derniers jours

function AlertSettingsPage() {
  const { t, tp, locale } = useI18n();
  const navigate = useNavigate();
  const [rules, setRules] = useState<AlertRule[]>([]);
  // Journal : alertes actives et résolues des 7 derniers jours
  const [events, setEvents] = useState<AlertEvent[] | null>(null);
  const [ackId, setAckId] = useState<number | null>(null);
  const [devices, setDevices] = useState<DeviceOverview[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // La télémétrie est secondaire : son échec ne doit pas empêcher le réglage.
      const [rulesRes, devicesRes, eventsRes] = await Promise.allSettled([
        alertsApi.listRules(),
        wattelEMApi.getOverview(),
        alertsApi.list(HISTORY_HOURS),
      ]);
      setEvents(eventsRes.status === 'fulfilled' ? eventsRes.value.data ?? [] : null);
      if (rulesRes.status === 'rejected') throw rulesRes.reason;
      const list = rulesRes.value.data ?? [];
      setRules(list);
      setDevices(devicesRes.status === 'fulfilled' ? devicesRes.value : []);

      const d: Record<string, Draft> = {};
      const groups = new Map<string, MetricGroup>();
      for (const r of list) {
        const key = `${r.deviceSn}|${r.metric}`;
        if (!groups.has(key)) groups.set(key, { metric: r.metric, max: null, min: null });
        const g = groups.get(key)!;
        if (r.operator === 'gt') g.max = r; else g.min = r;
      }
      for (const [key, g] of groups) d[key] = groupDraft(g);
      setDrafts(d);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Valeur mesurée en ce moment, ou null si l'appareil ne la remonte pas. */
  const liveValue = useCallback((deviceSn: string, metric: string): number | null => {
    const dev = devices.find(x => x.device_sn === deviceSn);
    if (!dev?.online) return null;
    const key = METRIC_UI[metric]?.telemetryKey;
    if (!key) return null;
    const raw = dev.current?.[key];
    if (raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }, [devices]);

  // installation → appareil → mesure
  const tree = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byInst = new Map<string, Map<string, MetricGroup[]>>();
    const groups = new Map<string, MetricGroup>();

    for (const r of rules) {
      if (!r.installationName) continue;
      const device = (displaySn(r.deviceSn) || r.deviceSn).toLowerCase();
      if (q && !device.includes(q) && !r.deviceSn.toLowerCase().includes(q)
          && !r.installationName.toLowerCase().includes(q)) continue;
      const key = `${r.deviceSn}|${r.metric}`;
      if (!groups.has(key)) {
        const g: MetricGroup = { metric: r.metric, max: null, min: null };
        groups.set(key, g);
        if (!byInst.has(r.installationName)) byInst.set(r.installationName, new Map());
        const devs = byInst.get(r.installationName)!;
        if (!devs.has(r.deviceSn)) devs.set(r.deviceSn, []);
        devs.get(r.deviceSn)!.push(g);
      }
      const g = groups.get(key)!;
      if (r.operator === 'gt') g.max = r; else g.min = r;
    }
    return [...byInst.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rules, search]);

  /** Mesure actuellement hors de la plage réglée (seuils enregistrés). */
  const isBreaching = useCallback((deviceSn: string, g: MetricGroup): boolean => {
    const live = liveValue(deviceSn, g.metric);
    if (live === null) return false;
    const ref = g.max ?? g.min;
    if (!ref?.enabled) return false;
    return (!!g.max && live > g.max.threshold) || (!!g.min && live < g.min.threshold);
  }, [liveValue]);

  // Chiffres du bandeau
  const allGroups = tree.flatMap(([, devs]) => [...devs.entries()].flatMap(([sn, gs]) => gs.map(g => ({ sn, g }))));
  const watchedDevices = tree.reduce((n, [, devs]) => n + devs.size, 0);
  const enabledRules = rules.filter(r => r.enabled && r.installationName).length;
  const outOfRange = allGroups.filter(({ sn, g }) => isBreaching(sn, g)).length;
  const active = (events ?? []).filter(e => e.isActive);
  const resolved = (events ?? []).filter(e => !e.isActive);
  const critical = active.filter(e => e.severity === 'critical').length;
  const journal = [...active, ...resolved.sort((a, b) => (b.resolvedAt ?? '').localeCompare(a.resolvedAt ?? ''))].slice(0, 12);
  const when = (iso: string | null) => iso ? new Date(iso).toLocaleString(locale, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '';

  const acknowledge = async (id: number) => {
    setAckId(id);
    try {
      await alertsApi.acknowledge(id);
      setEvents(prev => (prev ?? []).map(e => e.id === id ? { ...e, acknowledgedAt: new Date().toISOString() } : e));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setAckId(null);
    }
  };

  const patch = (key: string, part: Partial<Draft>) => {
    setDrafts(prev => ({ ...prev, [key]: { ...prev[key], ...part } }));
    setSavedKey(null);
  };

  const save = async (deviceSn: string, g: MetricGroup) => {
    const key = `${deviceSn}|${g.metric}`;
    const d = drafts[key];
    if (!d) return;
    const minutes = Number(d.durationMin);
    if (!Number.isInteger(minutes) || minutes < 0) { setError(t('team_alerts_invalidDuration')); return; }
    if (g.max && Number.isNaN(Number(d.max))) { setError(t('team_alerts_invalidMax')); return; }
    if (g.min && Number.isNaN(Number(d.min))) { setError(t('team_alerts_invalidMin')); return; }
    if (g.max && g.min && Number(d.min) >= Number(d.max)) {
      setError(t('team_alerts_minBelowMax')); return;
    }

    setSavingKey(key);
    try {
      const common = { minDurationS: minutes * 60, severity: d.severity, enabled: d.enabled };
      const writes: Promise<unknown>[] = [];
      if (g.max) writes.push(alertsApi.updateRule(g.max.id, { ...common, threshold: Number(d.max) }));
      if (g.min) writes.push(alertsApi.updateRule(g.min.id, { ...common, threshold: Number(d.min) }));
      await Promise.all(writes);
      setRules(prev => prev.map(r => {
        if (r.deviceSn !== deviceSn || r.metric !== g.metric) return r;
        const threshold = r.operator === 'gt' ? Number(d.max) : Number(d.min);
        return { ...r, threshold, minDurationS: minutes * 60, severity: d.severity, enabled: d.enabled };
      }));
      setSavedKey(key);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingKey(null);
    }
  };


  return (
    <RoleGuard orgRoles={['org_admin', 'maintenance']}>
      <div className="max-w-7xl mx-auto space-y-6">

        {/* ── Bandeau à widgets ── */}
        <header className="relative overflow-hidden rounded-3xl bg-[#0b2a4d] text-white p-6 shadow-zce-lg">
          <div aria-hidden className="absolute inset-0" style={{ background: 'radial-gradient(60% 90% at 90% 0%, rgba(248,113,113,.34), transparent 60%), linear-gradient(120deg, #0d3b66, #082040)' }} />
          <div aria-hidden className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-xs text-white/70">{t('alr_caption')}</p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-heading tracking-tight mt-1">{t('team_alerts_title')}</h1>
              <p className="text-white/70 text-sm mt-0.5">{t('team_alerts_subtitle')}</p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="search"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder={t('team_alerts_search')}
                aria-label={t('team_alerts_search')}
                className="w-56 h-10 text-sm bg-white/10 border border-white/20 rounded-xl px-3 text-white placeholder:text-white/55 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 focus:bg-white/15 transition"
              />
              <button
                onClick={load}
                disabled={loading}
                aria-label={t('team_refresh')}
                className="w-10 h-10 inline-flex items-center justify-center text-white bg-white/10 border border-white/20 rounded-xl hover:bg-white/20 disabled:opacity-40 transition"
              >
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>
          </div>
          <div className="relative grid gap-3 mt-5 grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-2xl border px-4 py-3.5 min-w-0 ${active.length ? 'border-red-300/45 bg-red-400/10' : 'border-white/15 bg-white/[0.06]'}`}>
              <div className="text-xs text-white/75">{t('alr_w_active')}</div>
              <div className="font-mono tabular-nums text-[30px] font-semibold mt-1">{events ? active.length : '—'}</div>
              <div className="text-[11px] text-white/60 mt-0.5">{events ? t('alr_w_active_foot', { critical, warning: active.length - critical }) : t('alr_unavailable')}</div>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3.5 min-w-0">
              <div className="text-xs text-white/75">{t('alr_w_resolved')}</div>
              <div className="font-mono tabular-nums text-[30px] font-semibold mt-1">{events ? resolved.length : '—'}</div>
              <div className="text-[11px] text-white/60 mt-0.5">{t('alr_w_resolved_foot')}</div>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3.5 min-w-0">
              <div className="text-xs text-white/75">{t('alr_w_devices')}</div>
              <div className="font-mono tabular-nums text-[30px] font-semibold mt-1">{loading ? '—' : watchedDevices}</div>
              <div className="text-[11px] text-white/60 mt-0.5">{t('alr_w_devices_foot', { n: enabledRules })}</div>
            </div>
            <div className={`rounded-2xl border px-4 py-3.5 min-w-0 ${outOfRange ? 'border-amber-300/45 bg-amber-300/10' : 'border-white/15 bg-white/[0.06]'}`}>
              <div className="text-xs text-white/75">{t('alr_w_out')}</div>
              <div className="font-mono tabular-nums text-[30px] font-semibold mt-1">{loading ? '—' : outOfRange}</div>
              <div className="text-[11px] text-white/60 mt-0.5">{t('alr_w_out_foot')}</div>
            </div>
          </div>
        </header>

        {error && (
          <div className="flex items-start gap-2.5 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3">
            <svg className="w-4 h-4 text-rose-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-rose-700">{error}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-6 items-start">
        <div className="flex-[999_1_640px] min-w-0 space-y-8">
        {!loading && tree.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center">
            <p className="text-base font-semibold text-zce-navy">{t('team_alerts_none')}</p>
            <p className="text-sm text-zce-gray-muted mt-1">
              {search ? t('team_noResults') : t('team_alerts_noDevices')}
            </p>
          </div>
        )}

        {tree.map(([installationName, deviceMap]) => (
          <section key={installationName} className="space-y-4">
            {/* Titre d'installation : discret, il structure sans dominer */}
            <div className="flex items-center gap-3">
              <h2 className="font-semibold text-zce-navy text-sm">{installationName}</h2>
              <span className="text-xs text-zce-gray-muted">
                {tp('team_alerts_devices', deviceMap.size)}
              </span>
              <span className="flex-1 h-px bg-gray-200" />
            </div>

            {[...deviceMap.entries()].map(([deviceSn, metricGroups]) => (
              <article key={deviceSn} className="bg-white rounded-xl shadow-zce border border-gray-100 overflow-hidden">
                {/* Liseré navy à gauche : rappel du thème sans texte bleu-lien */}
                <div className="px-6 py-3.5 bg-zce-gray-light border-b border-gray-100 border-l-4 border-l-zce-navy flex items-center justify-between gap-3">
                  <h3 className="font-semibold text-zce-navy text-sm tracking-tight">
                    {displaySn(deviceSn) || deviceSn}
                  </h3>
                  {(() => {
                    const n = metricGroups.filter(g => isBreaching(deviceSn, g)).length;
                    return n
                      ? <span className="shrink-0 h-6 px-2.5 rounded-full inline-flex items-center text-xs font-semibold bg-rose-50 text-rose-700">{t('alr_dev_out', { n })}</span>
                      : <span className="shrink-0 h-6 px-2.5 rounded-full inline-flex items-center text-xs font-semibold bg-emerald-50 text-emerald-700">{t('alr_dev_ok')}</span>;
                  })()}
                </div>

                <div className="divide-y divide-gray-100">
                  {metricGroups.map(g => {
                    const key = `${deviceSn}|${g.metric}`;
                    const d = drafts[key];
                    if (!d) return null;
                    const ui = METRIC_UI[g.metric];
                    const unit = METRIC_LABELS[g.metric]?.unit ?? '';
                    const label = metricLabel(g.metric, t);
                    const live = liveValue(deviceSn, g.metric);
                    const dirty = !draftEquals(g, d);
                    const maxN = Number(d.max);
                    const minN = Number(d.min);
                    const hasMax = !!g.max && !Number.isNaN(maxN);
                    const hasMin = !!g.min && !Number.isNaN(minN);
                    const breaching = live !== null && ((hasMax && live > maxN) || (hasMin && live < minN));

                    // Bornes de la zone sûre, en % de l'échelle.
                    const safeStart = hasMin && ui ? pct(minN, ui.domain) : 0;
                    const safeEnd = hasMax && ui ? pct(maxN, ui.domain) : 100;

                    // Un seul statut, lisible d'un coup d'oeil.
                    const status = !d.enabled
                      ? { label: t('team_alerts_statusDisabled'), dot: 'bg-gray-300', text: 'text-gray-400' }
                      : live === null
                        ? { label: t('team_alerts_statusNotMeasured'), dot: 'bg-gray-300', text: 'text-gray-400' }
                        : breaching
                          ? { label: t('team_alerts_statusAlert'), dot: 'bg-rose-500', text: 'text-rose-600' }
                          : { label: t('team_alerts_statusNormal'), dot: 'bg-emerald-500', text: 'text-emerald-600' };

                    return (
                      <div key={key} className={`px-6 py-4 transition-opacity ${d.enabled ? '' : 'opacity-60'}`}>

                        {/* Ligne 1 — mesure, valeur, statut, interrupteur */}
                        <div className="flex items-center justify-between gap-4 mb-4">
                          <div className="flex items-baseline gap-3 min-w-0">
                            <span className="text-[11px] font-bold uppercase tracking-wider text-zce-gray-muted">
                              {label}
                            </span>
                            {live !== null && (
                              <span className={`text-2xl font-bold font-heading tabular-nums ${breaching ? 'text-rose-600' : 'text-zce-navy'}`}>
                                {live.toFixed(ui?.decimals ?? 1)}
                                <span className="text-sm font-normal text-zce-gray-muted ml-1">{unit}</span>
                              </span>
                            )}
                          </div>

                          <div className="flex items-center gap-4 flex-shrink-0">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${status.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                              {status.label}
                            </span>
                            <button
                              onClick={() => patch(key, { enabled: !d.enabled })}
                              role="switch"
                              aria-checked={d.enabled}
                              aria-label={d.enabled ? t('team_alerts_disable', { label }) : t('team_alerts_enable', { label })}
                              className={`w-9 h-5 rounded-full transition-colors relative ${d.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            >
                              <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${d.enabled ? 'left-[18px]' : 'left-0.5'}`} />
                            </button>
                          </div>
                        </div>

                        {/* Ligne 2 — jauge : zone sûre en vert, zones d'alerte en rouge.
                            Sans mesure, tout passe en gris : une barre verte sur une
                            donnée absente affirmerait que tout va bien sans le savoir. */}
                        {/* Jauge bornée en largeur : étirée sur toute la carte, une
                            barre de 2 px se lisait comme un filet décoratif. */}
                        {ui && (
                          <div className="mb-4 max-w-xl">
                            <div className={`relative h-2.5 rounded-full ${live !== null && d.enabled ? 'bg-rose-100' : 'bg-gray-100'}`}>
                              <div
                                className={`absolute inset-y-0 rounded-full ${live !== null && d.enabled ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' : 'bg-gray-200'}`}
                                style={{ left: `${safeStart}%`, width: `${Math.max(0, safeEnd - safeStart)}%` }}
                              />
                              {hasMin && (
                                <span className="absolute -top-0.5 w-px h-3.5 bg-zce-navy/40" style={{ left: `${safeStart}%` }} />
                              )}
                              {hasMax && (
                                <span className="absolute -top-0.5 w-px h-3.5 bg-zce-navy/40" style={{ left: `${safeEnd}%` }} />
                              )}
                              {live !== null && (
                                <span
                                  className={`absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3.5 h-3.5 rounded-full border-2 border-white shadow-md ${breaching ? 'bg-rose-500' : 'bg-zce-navy'}`}
                                  style={{ left: `${pct(live, ui.domain)}%` }}
                                  title={t('team_alerts_currently', { value: `${live.toFixed(ui.decimals)} ${unit}` })}
                                />
                              )}
                            </div>
                            <div className="relative h-4 mt-1.5 text-[10px] tabular-nums text-zce-gray-muted">
                              <span className="absolute left-0">{ui.domain[0]}</span>
                              {hasMin && (
                                <span className="absolute -translate-x-1/2 font-semibold text-zce-navy" style={{ left: `${safeStart}%` }}>
                                  {minN}
                                </span>
                              )}
                              {hasMax && (
                                <span className="absolute -translate-x-1/2 font-semibold text-zce-navy" style={{ left: `${safeEnd}%` }}>
                                  {maxN}
                                </span>
                              )}
                              <span className="absolute right-0">{ui.domain[1]} {unit}</span>
                            </div>
                          </div>
                        )}

                        {/* Ligne 3 — la consigne, écrite comme une phrase */}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-2.5 text-sm text-zce-navy">
                          <span className="text-zce-gray-muted">{t('team_alerts_alertIf')}</span>
                          {g.min && g.max ? (
                            <>
                              <span className="font-medium">{t('team_alerts_outside')}</span>
                              <NumField value={d.min} onChange={v => patch(key, { min: v })} suffix={unit} />
                              <span className="text-zce-gray-muted">–</span>
                              <NumField value={d.max} onChange={v => patch(key, { max: v })} suffix={unit} />
                            </>
                          ) : (
                            <>
                              <span className="font-medium">{g.max ? t('team_alerts_above') : t('team_alerts_below')}</span>
                              <NumField
                                value={g.max ? d.max : d.min}
                                onChange={v => patch(key, g.max ? { max: v } : { min: v })}
                                suffix={unit}
                              />
                            </>
                          )}
                          <span className="text-zce-gray-muted">{t('team_alerts_for')}</span>
                          <NumField
                            value={d.durationMin}
                            onChange={v => patch(key, { durationMin: v })}
                            suffix="min"
                            width="w-20"
                          />

                          {/* Gravité et enregistrement dans le fil de la phrase :
                              le ml-auto d'avant creusait 600 px de vide au milieu. */}
                          <span className="text-zce-gray-muted">·</span>
                          <select
                            value={d.severity}
                            onChange={e => patch(key, { severity: e.target.value as AlertSeverity })}
                            aria-label={t('team_alerts_severity')}
                            className="text-xs font-medium text-zce-navy border border-gray-200 rounded-lg pl-2.5 pr-7 py-1.5 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-zce-blue/40"
                          >
                            {(['critical', 'warning', 'info'] as AlertSeverity[]).map(s => (
                              <option key={s} value={s}>{severityLabel(t, s)}</option>
                            ))}
                          </select>

                          {/* Le bouton n'existe que s'il y a quelque chose à faire :
                              un bouton gris permanent est un poids mort visuel. */}
                          {savedKey === key && !dirty && (
                            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 px-1">
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                              </svg>
                              {t('team_alerts_saved')}
                            </span>
                          )}
                          {dirty && (
                            <button
                              onClick={() => save(deviceSn, g)}
                              disabled={savingKey === key}
                              className="px-3.5 py-1.5 text-xs font-semibold text-white bg-zce-navy rounded-lg hover:bg-zce-blue disabled:opacity-50 transition-colors shadow-sm"
                            >
                              {savingKey === key ? t('team_alerts_saving') : t('team_alerts_save')}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </article>
            ))}
          </section>
        ))}

        <p className="text-xs text-zce-gray-muted leading-relaxed border-t border-gray-200 pt-5">
          {t('team_alerts_footer')}
        </p>
        </div>

        {/* ── Journal des alertes ── */}
        <aside className="flex-[1_1_300px] max-w-md w-full bg-white rounded-2xl border border-gray-200 shadow-zce p-5 space-y-3">
          <h2 className="font-bold text-zce-navy font-heading">{t('alr_journal')}</h2>
          {events === null && <p className="text-sm text-gray-500">{t('alr_unavailable')}</p>}
          {events && journal.length === 0 && <p className="text-sm text-gray-500">{t('alr_journal_empty')}</p>}
          {journal.map(e => {
            const accent = e.isActive ? (e.severity === 'critical' ? 'border-l-rose-500' : 'border-l-amber-400') : 'border-l-emerald-500';
            const value = e.valuePeak ?? e.valueTriggered;
            const unit = METRIC_LABELS[e.metric]?.unit ?? '';
            return (
              <div key={e.id} className={`rounded-xl bg-gray-50 border border-gray-100 border-l-4 ${accent} px-3.5 py-3`}>
                <div className="flex justify-between gap-2">
                  <span className="font-semibold text-sm text-zce-navy">{alertTitle(e, t)}{value != null ? ` · ${value} ${unit}` : ''}</span>
                  <span className="font-mono text-[11px] text-gray-400 whitespace-nowrap">{e.isActive ? when(e.triggeredAt) : when(e.resolvedAt)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-0.5">{displaySn(e.deviceSn) || e.deviceSn} · {e.isActive ? t('alr_state_active') : t('alr_state_resolved')}</div>
                {e.isActive && (
                  <div className="flex gap-2 mt-2.5">
                    {!e.acknowledgedAt && (
                      <button onClick={() => acknowledge(e.id)} disabled={ackId === e.id} className="h-8 px-3 rounded-lg bg-zce-navy text-white text-xs font-semibold hover:bg-zce-blue disabled:opacity-50">{t('alr_ack')}</button>
                    )}
                    {e.acknowledgedAt && <span className="h-8 inline-flex items-center text-xs font-semibold text-emerald-600">{t('alr_acked')}</span>}
                    <button onClick={() => navigate(`/wattel-em?sn=${encodeURIComponent(e.deviceSn)}`)} className="h-8 px-3 rounded-lg border border-gray-200 bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50">{t('alr_open_device')}</button>
                  </div>
                )}
              </div>
            );
          })}
        </aside>
        </div>
      </div>
    </RoleGuard>
  );
}

/** Champ numérique avec son unité intégrée — évite l'unité qui flotte à côté. */
function NumField({
  value, onChange, suffix, width = 'w-24',
}: {
  value: string;
  onChange: (v: string) => void;
  suffix?: string;
  width?: string;
}) {
  return (
    <span className={`relative inline-flex items-center ${width}`}>
      <input
        type="number"
        step="any"
        value={value}
        onChange={e => onChange(e.target.value)}
        // [appearance:textfield] masque les flèches natives : elles mangeaient
        // la place du chiffre dans les champs étroits (« 5 min » illisible).
        className={`w-full text-sm font-medium text-zce-navy tabular-nums border border-gray-200 rounded-lg py-1.5 pl-2.5 bg-white focus:outline-none focus:ring-2 focus:ring-zce-blue/40 focus:border-zce-blue transition [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${suffix ? 'pr-8' : 'pr-2.5'}`}
      />
      {suffix && (
        <span className="absolute right-2.5 text-[11px] text-zce-gray-muted pointer-events-none">
          {suffix}
        </span>
      )}
    </span>
  );
}

export default AlertSettingsPage;
